import { Step, StepLabel, Stepper } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';
import StopIcon from '@material-ui/icons/Stop';
import RemoveIcon from '@material-ui/icons/Remove';
import type { ActivityLogPhase } from '../../interfaces/ui';
import './styles/executionStepper.css';

const PHASES = [
  { id: 'plan', label: 'Plan' },
  { id: 'generate', label: 'Generate' },
  { id: 'heal', label: 'Heal' },
  { id: 'run', label: 'Run' },
  { id: 'report', label: 'Report' },
];

const PHASE_ALIASES: Record<string, string> = {
  context: 'plan',
  plan: 'plan',
  generate: 'generate',
  heal: 'heal',
  'scout & heal': 'heal',
  'scout-heal': 'heal',
  run: 'run',
  execute: 'run',
  analysis: 'report',
  analyze: 'report',
  report: 'report',
  complete: 'report',
};

const normalizePhase = (phase: string) =>
  PHASE_ALIASES[phase.trim().toLowerCase()] || phase.trim().toLowerCase();

const isCompletedStatus = (status: string) =>
  ['completed', 'complete', 'success', 'successful'].includes(
    status.trim().toLowerCase(),
  );

export interface ExecutionStepIconProps {
  status: 'completed' | 'failed' | 'stopped' | 'skipped' | 'active' | 'pending';
}

export const ExecutionStepIcon = ({ status }: ExecutionStepIconProps) => (
  <span
    className={`ec-stepper-node ec-stepper-node-${status}`}
    aria-hidden="true"
  >
    {status === 'completed' && <CheckIcon />}
    {status === 'failed' && <ErrorIcon />}
    {status === 'stopped' && <StopIcon />}
    {status === 'skipped' && <RemoveIcon />}
    {status === 'active' && <span className="ec-stepper-spinner" />}
    {status === 'pending' && <span className="ec-stepper-pending-dot" />}
  </span>
);

export default function ExecutionStepper({
  activityLog,
}: {
  activityLog: ActivityLogPhase[];
}) {
  const activityByPhase = new Map(
    activityLog.map(entry => [normalizePhase(entry.phase), entry]),
  );
  const lastEntry = activityLog[activityLog.length - 1];
  const activePhase = lastEntry && !lastEntry.endTime ? lastEntry : null;

  return (
    <div className="ec-stepper-section">
      <Stepper alternativeLabel className="ec-stepper-container">
        {PHASES.map(stage => {
          const activity = activityByPhase.get(stage.id);
          const status =
            activePhase && normalizePhase(activePhase.phase) === stage.id
              ? 'active'
              : isCompletedStatus(activity?.status || '')
              ? 'completed'
              : 'pending';

          return (
            <Step key={stage.id} completed={status === 'completed'}>
              <StepLabel
                StepIconComponent={() => <ExecutionStepIcon status={status} />}
                className={`ec-stepper-label${
                  status === 'pending' ? ' ec-stepper-label-pending' : ''
                }`}
              >
                {stage.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}
